/**
 * The Strat strip's Why tab (`docs/plans/strategy-lists.md` phase 5, placed
 * beside History by the owner 2026-10-27): a month's readings and margins as
 * plain words, grouped by the building block that produced them.
 *
 * Keys are the engine's (`qv-data strategies/engines.py`): `[block.]name`,
 * where a blended strategy prefixes each part's keys with its block. An
 * unknown name is shown sentence-cased rather than dropped — a new rule should
 * appear the day it ships, not the day somebody labels it.
 */
import type { StrategyWhy } from "../api/symbols";

const BLOCKS: Record<string, string> = {
  carrier: "Credit",
  catalyst: "Macro",
  "catalyst-200": "Macro (200-day)",
  triad: "Trend",
  gpmv: "Breadth",
  "": "Rules",
};

const NAMES: Record<string, string> = {
  spread: "HY credit spread",
  spread_6m_high: "HY spread, 6-month high",
  spread_na: "HY spread missing",
  breakeven: "5-yr inflation expectation",
  breakeven_3m: "Inflation expectation, 3 months ago",
  growth: "Growth",
  growth_gap: "Growth gap",
  inflation_level: "Inflation level",
  inflation_rising: "Inflation rising",
  momentum: "Momentum",
  trigger: "Trigger",
  leverage: "Leverage",
  leverage_months: "Months leveraged",
  qqq_off_high: "QQQ off its high",
  canary: "Canary",
  bond_share: "Bond share",
  breadth: "Breadth",
};

/** Under this a rule is a near call — `close.*` on the server, 0.5%. */
const CLOSE = 0.005;
const RUNS = ["preview", "near-close", "close", "confirm"];

export interface WhyLine {
  label: string;
  value: string;
}
export interface WhyPart {
  label: string;
  readings: WhyLine[];
  margins: (WhyLine & { close: boolean })[];
}
export interface WhyView {
  parts: WhyPart[];
  changed: { label: string; changed: boolean }[];
}

function split(key: string): [string, string] {
  const dot = key.lastIndexOf(".");
  return dot < 0 ? ["", key] : [key.slice(0, dot), key.slice(dot + 1)];
}

function nameOf(name: string): string {
  if (NAMES[name]) return NAMES[name]!;
  const words = name.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Units, measured on the artifact 2026-10-27: a fraction, a level already in %, or a 0/1 flag. */
const FRACTION = new Set(["growth_gap", "momentum", "qqq_off_high", "bond_share"]);
const PERCENT = new Set(["spread", "spread_6m_high", "breakeven", "breakeven_3m"]);
const FLAG = new Set(["leverage", "trigger", "spread_na"]);

function reading(name: string, v: number): string {
  if (FRACTION.has(name)) return `${(v * 100).toFixed(1)}%`;
  if (PERCENT.has(name)) return `${v.toFixed(2)}%`;
  if (FLAG.has(name)) return v ? "yes" : "no";
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function margin(v: number): string {
  return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`;
}

export function whyView(why: StrategyWhy): WhyView {
  const parts = new Map<string, WhyPart>();
  const part = (block: string) => {
    let p = parts.get(block);
    if (!p) {
      p = { label: BLOCKS[block] ?? nameOf(block), readings: [], margins: [] };
      parts.set(block, p);
    }
    return p;
  };
  for (const [key, v] of Object.entries(why.readings)) {
    const [block, name] = split(key);
    part(block).readings.push({ label: nameOf(name), value: reading(name, v) });
  }
  const close = new Set(why.close);
  for (const [key, v] of Object.entries(why.margins)) {
    const [block, name] = split(key);
    part(block).margins.push({ label: nameOf(name), value: margin(v), close: close.has(key) || Math.abs(v) < CLOSE });
  }
  const changed = Object.entries(why.changedSince)
    .sort(([a], [b]) => RUNS.indexOf(a) - RUNS.indexOf(b))
    .map(([stage, c]) => ({ label: `since ${stage}`, changed: c }));
  return { parts: [...parts.values()], changed };
}
